import { useEffect, useState } from 'react'
import { Alert, AlertTitle, Box, Typography } from '@mui/material'
import { Schedule } from '@mui/icons-material'
import { api } from '../../services/api'
import { formatRelativeTime } from '../../utils/format'

// Date and time in the schedule's zone (the plant's), not the browser's: "Tue,
// Oct 6, 5:00 AM CDT". The planner sees the same time the job runs at.
export function formatInZone(iso, timeZone) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZone, timeZoneName: 'short',
    })
  } catch {
    return new Date(iso).toLocaleString('en-US')
  }
}

// FAR-58 (option 2): tells planners that a Base Plan is built automatically
// every day, and when. The wording and time come from GET /base-plans/schedule,
// which reads the same Terraform setting that runs the schedule, so this can't
// say 5 AM while the job runs at 6.
//
// Also shows the latest Base Plan for the plant, so a planner can see the
// daily run actually happened.
export default function BasePlanScheduleNotice({ plantCode, compact = false }) {
  const [schedule, setSchedule] = useState(null)
  const [latest, setLatest] = useState(undefined) // undefined = not loaded

  useEffect(() => {
    let cancelled = false
    api.get('/base-plans/schedule')
      .then((s) => { if (!cancelled) setSchedule(s) })
      .catch(() => { if (!cancelled) setSchedule(null) })
    if (plantCode) {
      api.get(`/base-plans?plant=${encodeURIComponent(plantCode)}`)
        .then((list) => { if (!cancelled) setLatest(Array.isArray(list) && list.length ? list[0] : null) })
        .catch(() => { if (!cancelled) setLatest(undefined) })
    }
    return () => { cancelled = true }
  }, [plantCode])

  // Never show an invented schedule: if the API can't say, show nothing.
  if (!schedule) return null

  const latestLine = latest === undefined ? null
    : latest === null
      ? 'No Base Plan has been built for this plant yet.'
      : `Latest Base Plan: ${formatInZone(latest.createdAt, schedule.timezone)} (${formatRelativeTime(latest.createdAt)}), ${latest.trigger === 'scheduled' ? 'built by the daily run' : `generated by ${latest.createdBy}`}.`

  return (
    <Alert
      severity="info"
      icon={<Schedule fontSize="small" />}
      sx={{ mb: compact ? 2 : 3, py: 0.5 }}
      data-testid="base-plan-schedule"
    >
      {!compact && <AlertTitle sx={{ mb: 0.25 }}>Daily Base Plan</AlertTitle>}
      <Typography variant="body2" component="span">{schedule.description}</Typography>
      {schedule.enabled && schedule.nextRunAt && (
        <Typography variant="body2" component="span"> Next run: {formatInZone(schedule.nextRunAt, schedule.timezone)}.</Typography>
      )}
      {latestLine && (
        <Box component="span" sx={{ display: 'block', mt: 0.25, color: 'text.secondary' }}>
          <Typography variant="caption">{latestLine}</Typography>
        </Box>
      )}
    </Alert>
  )
}
